// =============================================================================
// MeuDriver — Batch P3: AnnouncementBanner
// Exibe anúncios globais da plataforma no topo dos layouts autenticados.
// Severity controla a cor; botão X dispensa individualmente.
// =============================================================================

import { X, Info, AlertTriangle, AlertOctagon } from 'lucide-react'
import { AnimatePresence, motion } from 'framer-motion'
import { cn } from '@/lib/utils'
import { useActiveAnnouncements, useDismissAnnouncement } from '@/hooks/useAnnouncements'
import type { AnnouncementSeverity } from '@/hooks/useAnnouncements'

// ---------------------------------------------------------------------------
// Helpers de estilo por severity
// ---------------------------------------------------------------------------

interface SeverityConfig {
  bg: string
  border: string
  text: string
  icon: typeof Info
  iconClass: string
}

const SEVERITY: Record<AnnouncementSeverity, SeverityConfig> = {
  info: {
    bg:        'bg-blue-50 dark:bg-blue-950/40',
    border:    'border-blue-200 dark:border-blue-800',
    text:      'text-blue-800 dark:text-blue-200',
    icon:      Info,
    iconClass: 'text-blue-500',
  },
  warning: {
    bg:        'bg-yellow-50 dark:bg-yellow-950/40',
    border:    'border-yellow-200 dark:border-yellow-800',
    text:      'text-yellow-800 dark:text-yellow-200',
    icon:      AlertTriangle,
    iconClass: 'text-yellow-500',
  },
  critical: {
    bg:        'bg-red-50 dark:bg-red-950/40',
    border:    'border-red-200 dark:border-red-800',
    text:      'text-red-800 dark:text-red-200',
    icon:      AlertOctagon,
    iconClass: 'text-red-500',
  },
}

// ---------------------------------------------------------------------------
// Componente
// ---------------------------------------------------------------------------

export function AnnouncementBanner() {
  const { data: announcements = [], isLoading } = useActiveAnnouncements()
  const dismiss = useDismissAnnouncement()

  if (isLoading || announcements.length === 0) return null

  return (
    <div className="flex flex-col gap-0">
      <AnimatePresence initial={false}>
        {announcements.map((ann) => {
          const cfg = SEVERITY[ann.severity] ?? SEVERITY.info
          const Icon = cfg.icon

          return (
            <motion.div
              key={ann.id}
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.2 }}
              className={cn(
                'border-b px-4 py-2.5',
                cfg.bg,
                cfg.border,
              )}
            >
              <div className="max-w-7xl mx-auto flex items-start gap-3">
                <Icon className={cn('h-4 w-4 mt-0.5 shrink-0', cfg.iconClass)} />
                <div className="flex-1 min-w-0">
                  <p className={cn('text-sm font-semibold', cfg.text)}>
                    {ann.title}
                  </p>
                  <p className={cn('text-xs mt-0.5', cfg.text, 'opacity-80')}>
                    {ann.body}
                  </p>
                </div>
                <button
                  type="button"
                  data-testid={`dismiss-announcement-${ann.id}`}
                  onClick={() => dismiss.mutate(ann.id)}
                  className={cn(
                    'shrink-0 rounded-md p-1 transition-colors',
                    cfg.text,
                    'opacity-60 hover:opacity-100',
                  )}
                  aria-label="Fechar anúncio"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>
            </motion.div>
          )
        })}
      </AnimatePresence>
    </div>
  )
}
